import type { World } from '@ecs/world';
import type { PlatformRenderStyle } from '@game/systems/render/createPlatformGraphicsSyncSystem';
import type { AmmoBarStyle } from '@game/systems/ui/createAmmoGraphicsSyncSystem';
import type { DashCooldownBarStyle } from '@game/systems/ui/createDashCooldownGraphicsSyncSystem';
import { createSpawnPlatform, type PlatformSpawnStyle } from '@game/scenes/characterPrototype/world/spawnPlatform';
import { createSpawnEnemy } from '@game/scenes/characterPrototype/world/spawnEnemy';
import { createSpawnProjectile } from '@game/scenes/characterPrototype/world/spawnProjectile';
import { createSpawnExplosion } from '@game/scenes/characterPrototype/world/spawnExplosion';
import { applyPlayerDamage as applyPlayerDamageEffect, applyTimeBoost as applyTimeBoostEffect } from './combat/effects';

export type CharacterPrototypeFactories = {
	applyPlayerDamage: (world: World, amount: number) => void;
	applyTimeBoost: (world: World, value: number, duration: number) => void;

	spawnPlatform: ReturnType<typeof createSpawnPlatform>;
	spawnEnemy: ReturnType<typeof createSpawnEnemy>;
	spawnProjectile: ReturnType<typeof createSpawnProjectile>;
	spawnExplosion: ReturnType<typeof createSpawnExplosion>;

	styles: {
		platform: PlatformRenderStyle & PlatformSpawnStyle;
		ammoBar: AmmoBarStyle;
		dashBar: DashCooldownBarStyle;
	};
};

export type CharacterPrototypeFactoriesConfig = {
	keys: {
		health: string;
		timescale: string;
		gameOver: string;
		gameOverReason: string;
	};

	ammoClipSize: number;

	projectileDefaults: {
		lifetime: number;
		size: number;
		fill: number;
		stroke: number;
	};

	explosionStyle: {
		fillColor: number;
		fillAlpha: number;
		strokeColor: number;
		strokeAlpha: number;
		strokeWidth: number;
	};

	styles: {
		platform: PlatformRenderStyle & PlatformSpawnStyle;
		ammoBar: AmmoBarStyle;
		dashBar: DashCooldownBarStyle;
	};
};

export function createCharacterPrototypeFactories(config: CharacterPrototypeFactoriesConfig): CharacterPrototypeFactories {
	const applyPlayerDamage = (world: World, amount: number) =>
		applyPlayerDamageEffect(
			world,
			{
				healthKey: config.keys.health,
				gameOverKey: config.keys.gameOver,
				gameOverReasonKey: config.keys.gameOverReason
			},
			amount,
			'dead'
		);

	const applyTimeBoost = (world: World, value: number, duration: number) =>
		applyTimeBoostEffect(world, config.keys.timescale, value, duration);

	const spawnPlatform = createSpawnPlatform(config.styles.platform);
	const spawnEnemy = createSpawnEnemy({ ammoClipSize: config.ammoClipSize });
	const spawnProjectile = createSpawnProjectile({
		defaultLifetime: config.projectileDefaults.lifetime,
		defaultSize: config.projectileDefaults.size,
		defaultFill: config.projectileDefaults.fill,
		defaultStroke: config.projectileDefaults.stroke
	});
	const spawnExplosion = createSpawnExplosion(config.explosionStyle);

	return {
		applyPlayerDamage,
		applyTimeBoost,
		spawnPlatform,
		spawnEnemy,
		spawnProjectile,
		spawnExplosion,
		styles: config.styles
	};
}


